import type { ReactElement, ReactNode } from 'react';
import { useGamesSite } from '../context';
import { PUBLIC_SITE_LINKS } from '../site-links';
import { PublicSiteShell } from './PublicSiteShell';

/**
 * The header and footer of a public page for a visitor who is not signed in
 * — a website's frame rather than the app's. (Signed in, the app's own header
 * frames the same page.) The skeleton is the shared `PublicSiteShell`.
 */
export function PublicSiteFrame({
  current,
  children,
}: {
  /** Which header link is the page being shown. */
  current?: 'games' | 'store';
  children: ReactNode;
}): ReactElement {
  const { Link, now, storeShown, gamesShown } = useGamesSite();
  const year = now ? new Date(now).getUTCFullYear() : null;
  return (
    <PublicSiteShell
      className="fg-frame"
      mainId="fg-main"
      skipLinkClassName="fg-skip"
      headerClassName="fg-frame__header"
      header={
        <div className="fg-frame__bar">
          <Link href={PUBLIC_SITE_LINKS.home} className="fg-wordmark" aria-label="Fias home">
            <img src="/fias-logo.png" alt="" width={86} height={36} />
          </Link>
          <nav aria-label="Main" className="fg-frame__nav">
            {gamesShown && (
              <Link
                href={PUBLIC_SITE_LINKS.games}
                className="fg-frame__link"
                aria-current={current === 'games' ? 'page' : undefined}
              >
                Games
              </Link>
            )}
            {storeShown && (
              <Link
                href={PUBLIC_SITE_LINKS.store}
                className="fg-frame__link"
                aria-current={current === 'store' ? 'page' : undefined}
              >
                Arche Store
              </Link>
            )}
            <Link href={PUBLIC_SITE_LINKS.docs} className="fg-frame__link">
              Docs
            </Link>
          </nav>
          <div className="fg-frame__actions">
            <Link href={PUBLIC_SITE_LINKS.signIn} className="fg-frame__link">
              Log in
            </Link>
            <Link href={PUBLIC_SITE_LINKS.join} className="fg-frame__join">
              Join Fias
            </Link>
          </div>
        </div>
      }
      footerClassName="fg-frame__footer"
      footer={
        <>
          <nav aria-label="Footer" className="fg-frame__footer-nav">
            <Link href={PUBLIC_SITE_LINKS.home} className="fg-frame__link">
              About Fias
            </Link>
            {gamesShown && (
              <Link href={PUBLIC_SITE_LINKS.games} className="fg-frame__link">
                Games
              </Link>
            )}
            {storeShown && (
              <Link href={PUBLIC_SITE_LINKS.store} className="fg-frame__link">
                Arche Store
              </Link>
            )}
            <Link href={PUBLIC_SITE_LINKS.docs} className="fg-frame__link">
              Docs
            </Link>
            <Link href={PUBLIC_SITE_LINKS.privacy} className="fg-frame__link">
              Privacy
            </Link>
            <Link href={PUBLIC_SITE_LINKS.terms} className="fg-frame__link">
              Terms
            </Link>
          </nav>
          <p className="fg-frame__legal">
            {year ? `© ${year} Fias. All rights reserved.` : 'Fias. All rights reserved.'}
          </p>
        </>
      }
    >
      {children}
    </PublicSiteShell>
  );
}

/** "Games › Word › Wordtrace" — the trail above a genre or game page. */
export function Breadcrumbs({
  trail,
}: {
  /** Each step but the last links; the last is the page itself. */
  trail: Array<{ label: string; href?: string }>;
}): ReactElement {
  const { Link } = useGamesSite();
  return (
    <nav aria-label="Breadcrumb" className="fg-crumbs">
      <ol>
        {trail.map((step, i) => (
          <li key={`${i}-${step.label}`}>
            {step.href && i < trail.length - 1 ? (
              <Link href={step.href}>{step.label}</Link>
            ) : (
              <span aria-current="page">{step.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
